import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle, ArrowLeft, ArrowUpRight, Building2, ChevronDown, Copy, Flag, Globe, ListPlus,
    MapPin, MoreHorizontal, Phone, Plus, Users, X,
} from "lucide-react";
import { toast } from "react-toastify";
import { IEstablishment, EstablishmentReportType } from "../../../interfaces/establishment";
import { EstablishmentMyContext, reportEstablishment } from "../../../interfaces/establishments";
import { EstablishmentLogo } from "../../../components/franchise/EstablishmentLogo";
import { AddToListPicker } from "../../../components/prospection/AddToListPicker";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { DropdownDivider } from "../../../components/ui/DropdownMenu";
import { REPORT_LABELS } from "../../../utils/reportConstants";
import { FounderCounts } from "../../../lib/founderCounts";
import { addressLines, displayName, mapsLinks, Person, TradingStatus } from "./model";
import { menuItem, TONE_DOT, useDismiss } from "./parts";

// Same look as <Button variant="primary" size="sm">, for the one action that is a link (tel:).
const PRIMARY_LINK =
    "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-transparent bg-ink px-3 font-sans text-sm font-medium text-ink-inverse transition-colors duration-150 hover:bg-ink/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";

interface HeaderProps {
    establishment: IEstablishment;
    status: TradingStatus;
    lead: Person | null;
    leadCounts?: FounderCounts;
    phone: string | null;
    context: EstablishmentMyContext | null;
    crmBusy: boolean;
    onAddToCrm: () => void;
    onListAdded: (list: { _id: string; name: string }) => void;
}

export function Header({ establishment: e, status, lead, leadCounts, phone, context, crmBusy, onAddToCrm, onListAdded }: HeaderProps) {
    const navigate = useNavigate();
    const franchise = (e as any).franchise as { _id: string; name: string } | null | undefined;
    const activity = e.cuisine_types?.[0] || e.categories?.[0] || e.registration?.naf_description;
    const seats = (e.operations?.seats_inside || 0) + (e.operations?.seats_terrace || 0);
    const facts = [
        activity,
        e.address?.city,
        seats > 0 ? `${seats} couverts` : null,
        e.operations?.has_terrace ? "terrasse" : null,
        e.price_range,
    ].filter(Boolean);
    const active = leadCounts?.active ?? 0;

    return (
        <header className="space-y-3">
            <div className="rounded-2xl border border-border bg-surface p-5">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="mb-4 inline-flex items-center gap-1.5 rounded-lg font-sans text-sm text-ink-muted transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Retour
                </button>

                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <div className="flex min-w-0 flex-1 items-start gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-border bg-surface-sunken">
                            <EstablishmentLogo name={e.name} franchise={franchise} nafCode={e.registration?.naf_code} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">{displayName(e.name)}</h1>
                                <Badge tone={status.tone}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${TONE_DOT[status.tone]}`} />
                                    {status.label}
                                </Badge>
                                {franchise?._id && (
                                    <a href={`/franchiseurs/${franchise._id}`} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                                        <Badge tone="neutral" className="hover:border-border-strong">
                                            Réseau {franchise.name}
                                            <ArrowUpRight className="h-3 w-3" />
                                        </Badge>
                                    </a>
                                )}
                            </div>
                            {facts.length > 0 && (
                                <p className="mt-1 font-sans text-sm text-ink-muted">{facts.join(" · ")}</p>
                            )}
                            {lead?.name && (
                                <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 font-sans text-sm text-ink">
                                    <Users className="h-4 w-4 text-ink-faint" />
                                    <span className="text-ink-muted">{lead.mandate}</span>
                                    <a href={`/signals/founder?founderName=${encodeURIComponent(lead.name)}`} className="font-medium hover:underline">
                                        {lead.name}
                                    </a>
                                    {lead.age !== null && <span className="text-ink-muted">· {lead.age} ans</span>}
                                    {active > 1 && (
                                        <a
                                            href={`/signals/founder?founderName=${encodeURIComponent(lead.name)}`}
                                            className="inline-flex items-center gap-1 font-medium text-ink underline decoration-border-strong underline-offset-2 hover:decoration-ink"
                                        >
                                            {active} établissements actifs
                                            <ArrowUpRight className="h-3.5 w-3.5" />
                                        </a>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                        {phone && !status.closed && (
                            <a href={`tel:${phone}`} className={PRIMARY_LINK}>
                                <Phone className="h-4 w-4" />
                                Appeler
                            </a>
                        )}
                        <AddMenu
                            establishmentId={e._id}
                            context={context}
                            crmBusy={crmBusy}
                            onAddToCrm={onAddToCrm}
                            onListAdded={onListAdded}
                        />
                        <MoreMenu establishment={e} />
                    </div>
                </div>
            </div>

            {status.closed && (
                <div role="alert" className="flex items-start gap-3 rounded-2xl border border-danger/20 bg-danger-subtle px-5 py-4">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
                    <div className="font-sans text-sm">
                        <p className="font-semibold text-danger">Cet établissement n'est plus en activité</p>
                        <p className="mt-0.5 text-ink-muted">
                            {status.detail}.
                            {e.registration?.siren && (
                                <>
                                    {" "}
                                    <a href={`/siren/${e.registration.siren}`} className="font-medium text-ink underline underline-offset-2">
                                        Voir les autres établissements de la société
                                    </a>
                                    {lead?.name && (
                                        <>
                                            {" "}ou{" "}
                                            <a href={`/signals/founder?founderName=${encodeURIComponent(lead.name)}`} className="font-medium text-ink underline underline-offset-2">
                                                ceux de {lead.name}
                                            </a>
                                        </>
                                    )}
                                    .
                                </>
                            )}
                        </p>
                    </div>
                </div>
            )}
        </header>
    );
}

/* ─── + Ajouter ─────────────────────────────────────────────────────────── */

function AddMenu({ establishmentId, context, crmBusy, onAddToCrm, onListAdded }: {
    establishmentId: string;
    context: EstablishmentMyContext | null;
    crmBusy: boolean;
    onAddToCrm: () => void;
    onListAdded: (list: { _id: string; name: string }) => void;
}) {
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    const ref = useDismiss(open, close);
    const prospect = context?.prospect;
    const lists = context?.lists || [];

    return (
        <AddToListPicker establishmentIds={[establishmentId]} onAdded={onListAdded}>
            {({ onClick: openListPicker }) => (
                <div ref={ref} className="relative">
                    <Button variant="secondary" size="sm" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
                        <Plus className="h-4 w-4" />
                        Ajouter
                        <ChevronDown className="h-4 w-4 text-ink-faint" />
                    </Button>
                    {open && (
                        <div role="menu" className="absolute right-0 top-full z-30 mt-1 w-64 rounded-2xl border border-border bg-surface p-2 shadow-float">
                            {prospect ? (
                                <a role="menuitem" href={`/kanban?openProspect=${prospect._id}`} target="_blank" rel="noopener noreferrer" onClick={close} className={menuItem}>
                                    <Users className="h-4 w-4 text-success" />
                                    <span className="flex-1">Voir dans mon CRM</span>
                                    <ArrowUpRight className="h-4 w-4 text-ink-faint" />
                                </a>
                            ) : (
                                <button role="menuitem" type="button" disabled={crmBusy} onClick={() => { close(); onAddToCrm(); }} className={`${menuItem} disabled:opacity-40`}>
                                    <Users className="h-4 w-4 text-ink-faint" />
                                    Suivre dans mon CRM
                                </button>
                            )}
                            <DropdownDivider />
                            {lists.slice(0, 3).map((l) => (
                                <a key={l._id} role="menuitem" href={`/prospection/${l._id}`} target="_blank" rel="noopener noreferrer" onClick={close} className={menuItem} title={l.name}>
                                    <ListPlus className="h-4 w-4 text-success" />
                                    <span className="flex-1 truncate">Voir dans « {l.name} »</span>
                                    <ArrowUpRight className="h-4 w-4 text-ink-faint" />
                                </a>
                            ))}
                            <button role="menuitem" type="button" onClick={(ev) => { openListPicker(ev); close(); }} className={menuItem}>
                                <ListPlus className="h-4 w-4 text-ink-faint" />
                                {lists.length > 0 ? "À une autre liste…" : "À une liste de prospection…"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </AddToListPicker>
    );
}

/* ─── ⋯ ─────────────────────────────────────────────────────────────────── */

function MoreMenu({ establishment: e }: { establishment: IEstablishment }) {
    const [open, setOpen] = useState(false);
    const [reporting, setReporting] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    const ref = useDismiss(open, close);
    const siren = e.registration?.siren;
    const { full } = addressLines(e);
    const maps = mapsLinks(e);

    return (
        <div ref={ref} className="relative">
            <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)} aria-label="Plus d'actions" aria-haspopup="menu" aria-expanded={open}>
                <MoreHorizontal className="h-4 w-4" />
            </Button>
            {open && (
                <div role="menu" className="absolute right-0 top-full z-30 mt-1 w-64 rounded-2xl border border-border bg-surface p-2 shadow-float">
                    {e.website_url && (
                        <a role="menuitem" href={e.website_url} target="_blank" rel="noopener noreferrer" onClick={close} className={menuItem}>
                            <Globe className="h-4 w-4 text-ink-faint" />
                            <span className="flex-1">Site web</span>
                            <ArrowUpRight className="h-4 w-4 text-ink-faint" />
                        </a>
                    )}
                    <a role="menuitem" href={maps.place} target="_blank" rel="noopener noreferrer" onClick={close} className={menuItem}>
                        <MapPin className="h-4 w-4 text-ink-faint" />
                        <span className="flex-1">Fiche Google Maps</span>
                        <ArrowUpRight className="h-4 w-4 text-ink-faint" />
                    </a>
                    {full && (
                        <button
                            role="menuitem"
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(full).then(() => toast.success("Adresse copiée")).catch(() => {});
                                close();
                            }}
                            className={menuItem}
                        >
                            <Copy className="h-4 w-4 text-ink-faint" />
                            Copier l'adresse
                        </button>
                    )}
                    {siren && (
                        <>
                            <DropdownDivider />
                            <a role="menuitem" href={`/siren/${siren}`} onClick={close} className={menuItem}>
                                <Building2 className="h-4 w-4 text-ink-faint" />
                                Établissements de la société
                            </a>
                            <a role="menuitem" href={`https://www.pappers.fr/entreprise/${siren}`} target="_blank" rel="noopener noreferrer" onClick={close} className={menuItem}>
                                <span className="h-4 w-4" />
                                <span className="flex-1">Pappers</span>
                                <ArrowUpRight className="h-4 w-4 text-ink-faint" />
                            </a>
                            <a role="menuitem" href={`https://www.societe.com/cgi-bin/search?champs=${siren}`} target="_blank" rel="noopener noreferrer" onClick={close} className={menuItem}>
                                <span className="h-4 w-4" />
                                <span className="flex-1">Societe.com</span>
                                <ArrowUpRight className="h-4 w-4 text-ink-faint" />
                            </a>
                        </>
                    )}
                    <DropdownDivider />
                    <button role="menuitem" type="button" onClick={() => { close(); setReporting(true); }} className={menuItem}>
                        <Flag className="h-4 w-4 text-ink-faint" />
                        Signaler une erreur
                    </button>
                </div>
            )}
            {reporting && <ReportDialog establishmentId={e._id} onClose={() => setReporting(false)} />}
        </div>
    );
}

function ReportDialog({ establishmentId, onClose }: { establishmentId: string; onClose: () => void }) {
    const [type, setType] = useState<EstablishmentReportType>("wrong_data");
    const [comment, setComment] = useState("");
    const [sending, setSending] = useState(false);
    const ref = useDismiss(true, onClose);

    const send = async () => {
        setSending(true);
        try {
            await reportEstablishment(establishmentId, type, comment.trim() || undefined);
            toast.success("Merci, le signalement a été envoyé.");
            onClose();
        } catch {
            toast.error("Le signalement n'a pas pu être envoyé.");
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 p-4">
            <div ref={ref} role="dialog" aria-modal="true" aria-label="Signaler une erreur" className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-float">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="font-sans text-base font-semibold text-ink">Signaler une erreur</h2>
                    <Button variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
                        <X className="h-4 w-4" />
                    </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {(Object.keys(REPORT_LABELS) as EstablishmentReportType[]).map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setType(t)}
                            aria-pressed={type === t}
                            className={`rounded-lg border px-3 py-2 text-left font-sans text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                type === t ? "border-ink bg-surface-sunken text-ink" : "border-border text-ink-muted hover:bg-surface-sunken"
                            }`}
                        >
                            {REPORT_LABELS[t]}
                        </button>
                    ))}
                </div>
                <textarea
                    value={comment}
                    onChange={(ev) => setComment(ev.target.value)}
                    placeholder="Précisez si besoin (facultatif)"
                    rows={3}
                    className="mt-4 w-full rounded-lg border border-border bg-surface px-3 py-2 font-sans text-sm text-ink placeholder:text-ink-faint focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                />
                <div className="mt-4 flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
                    <Button size="sm" onClick={send} disabled={sending}>Envoyer</Button>
                </div>
            </div>
        </div>
    );
}
